import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AppointmentInfoService } from './appointment-info.service';
import { SharedStorageService } from './shared-storage.service';
import { ModifyExamDietDialogComponent } from '../components/dialogs/modify-exam-diet-dialog/modify-exam-diet-dialog.component';
import { ModifyExamDietDialogModel } from '../interfaces/dialogs/modify-exam-diet-dialog.model';
import {
  PersonalCheckupSettingsRequest,
  BusinessCheckupSettingsRequest,
} from '../interfaces/appointment-info.model';
import { ExamItemName } from '../interfaces/reserve-confirm-survey.model';
import { AddExamInfo } from '../interfaces/reserve-add.model';
import { API_PARAMS } from '../constants/api.constants';
import { HealthCheckPlanResponse } from '../interfaces/my-reservation.model';
import { ModifyExamClothesDialogModel } from '../interfaces/dialogs/modify-exam-clothes-dialog.model';
import { ModifyExamClothesDialogComponent } from '../components/dialogs/modify-exam-clothes-dialog/modify-exam-clothes-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class DialogHelperService {
  private dialog = inject(MatDialog);
  private appointmentInfoService = inject(AppointmentInfoService);
  private sharedStorageService = inject(SharedStorageService);

  /**
   * 開啟修改飲食 Dialog
   * @param healthCheckPlan 健檢計畫資料
   * @param isPersonal 是否為個人預約
   * @param additionalData 額外資料 (caseNo, idNo, companyNo 等)
   */
  openModifyDietDialog(
    healthCheckPlan: HealthCheckPlanResponse | undefined,
    isPersonal: boolean,
    additionalData?: {
      caseNo?: string;
      idNo?: string;
      companyNo?: string;
    }
  ): Observable<ModifyExamDietDialogModel | undefined> {
    // 取得當前的 bookingInfo
    const bookingInfo = this.sharedStorageService.get('bookingInfo');
    
    // 準備加選檢項
    const addExamInfoList: Array<AddExamInfo> =
      this.sharedStorageService.get('addExamItemList') ?? [];
    const addList: Array<ExamItemName> =
      addExamInfoList?.filter((addExamInfo: AddExamInfo) => (addExamInfo.selectFlag ?? 1) === 1)
      .map((item: AddExamInfo) => ({
        examItem: item.examItem,
        examName: item.examName,
      })) ?? [];

    const userPlanRequest = this.sharedStorageService.get('userPlanRequest');
    const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');
    const myReservation = this.sharedStorageService.get('myReservation');

    let apiCall;

    if (isPersonal) {
      // 取得方案內檢項
      const examItemList =
        healthCheckPlan?.packageGroup.packageInfo.planList.flatMap(
          (planItem) =>
            planItem.examItemList.map((exam) => ({
              examItem: exam.examItem,
              examName: exam.examName,
              selectFlag: 1,
            }))
        ) ?? [];

      const request: PersonalCheckupSettingsRequest = {
        dataType: API_PARAMS.CHECKUP_SETTINGS_DATATYPE.DIET,
        caseNo: myReservation?.caseNo ?? additionalData?.caseNo ?? '',
        companyNo: '1150',
        orgNo: userPlanRequest?.orgNo ?? '',
        gender: userPlanRequest?.gender ?? '',
        idNo: userPlanRequest?.idNo ?? additionalData?.idNo ?? '',
        UUID: userPlanRequest?.uuid ?? '',
        bookDate: myReservation?.bookDate ?? '',
        examItemList: examItemList,
        addList: addList,
      };
      apiCall = this.appointmentInfoService.checkupSettings(request);
    } else {
      const idLoginRequest = this.sharedStorageService.get('idLoginRequest');
      const businessLoginInfo = this.sharedStorageService.get('businessLoginInfo');
      const createMemberRequest = this.sharedStorageService.get('createMemberRequest');
      const bookingInfo = this.sharedStorageService.get('bookingInfo');

      // 取得企業方案內檢項
      const examItemList =
        healthCheckPlan?.packageGroup.packageInfo.planList.flatMap(
          (planItem) =>
            planItem.examItemList.map((exam) => ({
              examItem: exam.examItem,
              examName: exam.examName,
            }))
        ) ?? [];

      const request: BusinessCheckupSettingsRequest = {
        dataType: API_PARAMS.CHECKUP_SETTINGS_DATATYPE.DIET,
        caseNo: myReservation?.caseNo ?? additionalData?.caseNo ?? '',
        companyNo:
          businessPlanRequest?.companyNo ??
          myReservation?.companyNo ??
          additionalData?.companyNo ??
          '',
        familyFlag:
          idLoginRequest?.identity ??
          businessLoginInfo?.identity ??
          myReservation?.familyFlag ??
          API_PARAMS.IDENTITY.EMPLOYEE,
        orgNo: businessPlanRequest?.orgNo ?? bookingInfo?.orgNo ?? '',
        gender: businessPlanRequest?.gender ?? createMemberRequest?.gender ?? '',
        idNo:
          idLoginRequest?.idNo ??
          createMemberRequest?.idNo ??
          additionalData?.idNo ??
          '',
        bookDate: myReservation?.bookDate ?? '',
        examItemList: examItemList,
        addList: addList,
      };
      apiCall = this.appointmentInfoService.businessCheckupSettings(request);
    }

    // 先打 API,取得資料後再開啟 dialog
    return apiCall.pipe(
      switchMap((res) => {
        // 從 API 回傳的資料中取得餐食選項
        const breakfasts = res.data?.breakfastInfos ?? [];
        const lunches = res.data?.lunchInfos ?? [];
        
        const dialogRef = this.dialog.open(ModifyExamDietDialogComponent, {
          panelClass: 'modify-dialog',
          data: {
            healthCheckPlan,
            isPersonal,
            additionalData,
            breakfast: bookingInfo?.breakfast ?? '',  // 傳入當前早餐值
            lunch: bookingInfo?.lunch ?? '',          // 傳入當前午餐值
            breakfasts,
            lunches,
            hasLunchOptions: lunches.length > 0
          }
        });
        
        return dialogRef.afterClosed();
      })
    );
  }

  /**
   * 開啟修改健檢服尺寸 Dialog
   * @param healthCheckPlan 健檢計畫資料
   * @param isPersonal 是否為個人預約
   * @param additionalData 額外資料
   */
  openModifyClothesDialog(
    healthCheckPlan: HealthCheckPlanResponse | undefined,
    isPersonal: boolean,
    additionalData?: {
      caseNo?: string;
      idNo?: string;
      companyNo?: string;
    }
  ): Observable<ModifyExamClothesDialogModel | undefined> {
    const addExamInfoList: Array<AddExamInfo> =
      this.sharedStorageService.get('addExamItemList') ?? [];
    const addList: Array<ExamItemName> =
      addExamInfoList?.filter((addExamInfo: AddExamInfo) => (addExamInfo.selectFlag ?? 1) === 1)
      .map((item: AddExamInfo) => ({
        examItem: item.examItem,
        examName: item.examName,
      })) ?? [];

    const userPlanRequest = this.sharedStorageService.get('userPlanRequest');
    const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');
    const myReservation = this.sharedStorageService.get('myReservation');

    let apiCall;

    if (isPersonal) {
      const examItemList =
        healthCheckPlan?.packageGroup.packageInfo.planList.flatMap(
          (planItem) =>
            planItem.examItemList.map((exam) => ({
              examItem: exam.examItem,
              examName: exam.examName,
              selectFlag: 1,
            }))
        ) ?? [];

      const request: PersonalCheckupSettingsRequest = {
        dataType: API_PARAMS.CHECKUP_SETTINGS_DATATYPE.CLOTHES_SIZE,
        caseNo: myReservation?.caseNo ?? additionalData?.caseNo ?? '',
        companyNo: '1150',
        orgNo: userPlanRequest?.orgNo ?? '',
        gender: userPlanRequest?.gender ?? '',
        idNo: userPlanRequest?.idNo ?? additionalData?.idNo ?? '',
        UUID: userPlanRequest?.uuid ?? '',
        bookDate: myReservation?.bookDate ?? '',
        examItemList: examItemList,
        addList: addList,
      };
      apiCall = this.appointmentInfoService.checkupSettings(request);
    } else {
      const idLoginRequest = this.sharedStorageService.get('idLoginRequest');
      const businessLoginInfo = this.sharedStorageService.get('businessLoginInfo');
      const createMemberRequest = this.sharedStorageService.get('createMemberRequest');
      const bookingInfo = this.sharedStorageService.get('bookingInfo');

      const examItemList =
        healthCheckPlan?.packageGroup.packageInfo.planList.flatMap(
          (planItem) =>
            planItem.examItemList.map((exam) => ({
              examItem: exam.examItem,
              examName: exam.examName,
            }))
        ) ?? [];

      const request: BusinessCheckupSettingsRequest = {
        dataType: API_PARAMS.CHECKUP_SETTINGS_DATATYPE.CLOTHES_SIZE,
        caseNo: myReservation?.caseNo ?? additionalData?.caseNo ?? '',
        companyNo:
          businessPlanRequest?.companyNo ??
          myReservation?.companyNo ??
          additionalData?.companyNo ??
          '',
        familyFlag:
          idLoginRequest?.identity ??
          businessLoginInfo?.identity ??
          myReservation?.familyFlag ??
          API_PARAMS.IDENTITY.EMPLOYEE,
        orgNo: businessPlanRequest?.orgNo ?? bookingInfo?.orgNo ?? '',
        gender: businessPlanRequest?.gender ?? createMemberRequest?.gender ?? '',
        idNo:
          idLoginRequest?.idNo ??
          createMemberRequest?.idNo ??
          additionalData?.idNo ??
          '',
        bookDate: myReservation?.bookDate ?? '',
        examItemList: examItemList,
        addList: addList,
      };
      apiCall = this.appointmentInfoService.businessCheckupSettings(request);
    }

    return apiCall.pipe(
      switchMap((res) => {
        const dialogRef = this.dialog.open(ModifyExamClothesDialogComponent, {
          panelClass: 'modify-dialog',
          data: {
            clothesSize: healthCheckPlan?.bookData?.clothesSize.uno ?? '',
            clothesSizes: (res.data?.sizeInfos ?? []).sort(
              (a, b) => Number(a.uno) - Number(b.uno)
            ),
          },
        });

        return dialogRef.afterClosed();
      })
    );
  }
}
